import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Music2 } from "lucide-react";
import { api } from "@/lib/api";
import { mediaUrl } from "@/lib/helpers";
import { Sparkline } from "@/components/wei/Ranking";
import { PageHero, SectionHeading, Skeleton } from "@/components/wei/Section";
import { VerifiedBadge, Flag, Movement, DemoTag } from "@/components/wei/Badges";

const GENRES = ["Afrobeats", "Amapiano", "Hip-Hop", "R&B", "Afro House", "Pop", "Gospel", "Electronic", "Jazz", "Film", "Comedy", "Dance"];

export function GenreChartCard({ chart, limit = 5, compact = true }) {
  return (
    <div data-testid={`genre-chart-${chart.genre.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="wei-card p-4 flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-serif text-xl font-semibold inline-flex items-center gap-2"><Music2 className="w-4 h-4 text-gold" /> {chart.genre}</h3>
        <span className="text-[11px] text-[#6B675F]">Top {Math.min(limit, chart.items.length)} · 7D</span>
      </div>
      <ol className="divide-y divide-[#EFEDE8] flex-1">
        {chart.items.slice(0, limit).map((p) => (
          <li key={p.slug} className="py-2 flex items-center gap-3">
            <span className="font-serif text-lg font-bold w-7 text-ink">{p.rank}</span>
            <img src={mediaUrl(p.image)} alt="" className="w-9 h-9 rounded-sm object-cover" loading="lazy" />
            <div className="min-w-0 flex-1">
              <Link to={`/registry/${p.slug}`} className="text-sm font-medium hover:text-bordeaux inline-flex items-center gap-1 truncate">{p.name} <VerifiedBadge status={p.verification_status} compact /></Link>
              <div className="text-[11px] text-[#6B675F] flex items-center gap-2 truncate">{p.category} · <Flag code={p.country_code} name={compact ? "" : p.country} className="!text-[11px]" /></div>
            </div>
            {!compact && <Sparkline data={(p.sparkline || []).map((v) => -v)} width={60} height={20} />}
            <div className="text-right"><div className="text-xs font-semibold tabular-nums">{p.score?.toFixed(1)}</div><Movement value={p.movement} /></div>
          </li>
        ))}
        {!chart.items.length && <li className="py-6 text-sm text-[#8A867E] text-center">No classified profiles in this genre yet.</li>}
      </ol>
      {compact && <Link to={`/charts?genre=${encodeURIComponent(chart.genre)}`} className="mt-3 text-xs font-medium text-bordeaux inline-flex items-center gap-1 hover:gap-2 transition-all">Full {chart.genre} chart <ArrowRight className="w-3.5 h-3.5" /></Link>}
    </div>
  );
}

export function GenreChartsHome() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/charts/genres", { params: { limit: 5 } }).then((r) => setData(r.data)).catch(() => setData([])); }, []);
  const picks = data ? ["Afrobeats", "Amapiano", "Hip-Hop", "Afro House"].map((g) => data.find((d) => d.genre === g)).filter(Boolean) : [];
  return (
    <section data-testid="genre-charts-section" className="bg-white border-y border-[#E6E3DC]">
      <div className="container-wei py-12">
        <SectionHeading eyebrow="Weekly genre charts" title="Genre Charts" to="/charts" linkLabel="All genres" id="genre-charts" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">{picks.length ? picks.map((c) => <GenreChartCard key={c.genre} chart={c} />) : Array(4).fill(null).map((_, i) => <Skeleton key={i} className="h-72" />)}</div>
      </div>
    </section>
  );
}

export default function Charts() {
  const [params, setParams] = useSearchParams();
  const genre = params.get("genre") || "";
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get("/charts/genres", { params: { genre: genre || undefined, limit: 10 } }).then((r) => setData(r.data)).catch(() => setData([])); }, [genre]);
  return (
    <main data-testid="charts-page">
      <PageHero eyebrow="WEI Genre Charts" title={genre ? `${genre} Top 10` : "Genre Charts"} description="Top 10 classified profiles per genre with weekly movement. Movement reflects change in WEI classification position over the past 7 days.">
        <div className="mt-5 flex flex-wrap gap-2">
          <button data-testid="genre-tab-all" onClick={() => setParams({})} className={`px-3.5 py-1.5 text-xs font-medium rounded-full border ${!genre ? "bg-ink text-white border-ink" : "border-[#D9D5CC] hover:border-ink"}`}>All genres</button>
          {GENRES.map((g) => <button key={g} data-testid={`genre-tab-${g.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setParams({ genre: g })} className={`px-3.5 py-1.5 text-xs font-medium rounded-full border ${genre === g ? "bg-bordeaux text-white border-bordeaux" : "border-[#D9D5CC] hover:border-bordeaux"}`}>{g}</button>)}
        </div>
        <div className="mt-4"><DemoTag /></div>
      </PageHero>
      <div className="container-wei py-10">
        {!data ? <Skeleton className="h-96" /> : genre ? (
          <div className="grid lg:grid-cols-[1fr_320px] gap-6">
            <GenreChartCard chart={data[0]} limit={10} compact={false} />
            <aside className="wei-card p-5 self-start">
              <div className="eyebrow text-gold text-[10px]">Weekly mover</div>
              {data[0].top_mover ? <><Link to={`/registry/${data[0].top_mover.slug}`} className="flex items-center gap-3 mt-3"><img src={mediaUrl(data[0].top_mover.image)} alt="" className="w-14 h-14 rounded-sm object-cover" /><div><div className="font-serif text-lg font-semibold">{data[0].top_mover.name}</div><div className="text-xs text-[#6B675F]">#{data[0].top_mover.previous_rank} → #{data[0].top_mover.rank} · <Movement value={data[0].top_mover.movement} /></div></div></Link><div className="mt-4"><Sparkline data={(data[0].top_mover.sparkline || []).map((v) => -v)} width={240} height={50} color="#8B001B" /></div></> : <p className="text-sm text-[#8A867E] mt-2">No chart data.</p>}
            </aside>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{data.map((c) => <GenreChartCard key={c.genre} chart={c} limit={10} />)}</div>
        )}
      </div>
    </main>
  );
}
